import {
  CollapsibleCard,
  Divider,
  HierarchyCard,
  Text
} from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useState } from 'react'
import { AnimationSliderType } from './AnimationSlider.types'
import {
  CardContainer,
  CollapsibleSection,
  Complement,
  Container,
  ImagesContainer,
  ImageWrapper,
  MediaControlContainer,
  SlideNumber,
  SlideTitle,
  Subtitle,
  SubtitleWrapper
} from './AnimationSlider.styles'
import StarterPopup from './StarterPopup'
import { useTranslation } from 'react-i18n-lite'
import useWindowDimensions from 'shared/hooks/useWindowDimensions'
import { SafeHtmlContent } from 'site/shared/components/SafeHtmlContent'
import Media from '../Media/Media'

const AnimationSlider = ({
  slides,
  subtitles,
  starterPopup,
  color,
  textColor
}: AnimationSliderType) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isAnimationStarted, setIsAnimationStarted] = useState(false)
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(false)

  const { t } = useTranslation()
  const { windowWidth } = useWindowDimensions()

  const isMobile = windowWidth < 992

  //preload images
  useEffect(() => {
    for (const slide of slides) {
      const imageElement = new Image()
      imageElement.src = isMobile
        ? slide.image.mobile.url
        : slide.image.desktop.url
    }
  }, [slides, isMobile])

  const navigateToNext = useCallback(() => {
    setCurrentSlide((prevIndex) => {
      if (prevIndex === slides.length - 1) {
        return prevIndex
      }

      if (prevIndex + 1 === slides.length - 1) {
        setIsAnimationPlaying(false)
      }

      return prevIndex + 1
    })
  }, [slides.length, setCurrentSlide, setIsAnimationPlaying])

  const navigateToSlide = (slide: number) => {
    setCurrentSlide(slide)

    if (slide === slides.length - 1) {
      setIsAnimationPlaying(false)
    }
  }

  const renderTitle = () => (
    <SlideTitle>
      <SlideNumber color={color} textColor={textColor}>
        <Text
          variant={isMobile ? 'content-emphasis-04' : 'content-emphasis-06'}
        >
          {currentSlide + 1}
        </Text>
      </SlideNumber>
      <Text variant={isMobile ? 'content-emphasis-04' : 'content-emphasis-06'}>
        {slides[currentSlide].des}
      </Text>
    </SlideTitle>
  )

  return (
    <>
      <Container>
        <StarterPopup
          onClick={() => {
            setIsAnimationStarted(true)
            setIsAnimationPlaying(true)
          }}
          color={color}
          {...starterPopup}
        />
        {!isMobile && (
          <ImageWrapper>
            <img
              src={slides[currentSlide].image.desktop.url}
              alt={slides[currentSlide].image.desktop.alt}
            />
          </ImageWrapper>
        )}
        {(!isMobile || isAnimationStarted) && (
          <CardContainer>
            <HierarchyCard spacingClass='extraLarge'>
              <Complement>
                {isMobile ? (
                  <>
                    <MediaControlContainer color={color}>
                      <Text variant='heading-emphasis-01' as='h1'>
                        {t('animation-slider.slider')}
                      </Text>
                      <Media
                        currentPosition={currentSlide}
                        total={slides.length}
                        onNavigateNext={navigateToNext}
                        onNavigate={navigateToSlide}
                        isPlaying={isAnimationPlaying}
                        setIsPlaying={setIsAnimationPlaying}
                        color={color}
                      />
                    </MediaControlContainer>
                    <CollapsibleSection
                      leftHeaderElement={renderTitle()}
                      heightClass='small'
                    >
                      <Text variant='bordertext-05'>
                        <SafeHtmlContent
                          html={slides[currentSlide].complement}
                        />
                      </Text>
                    </CollapsibleSection>
                  </>
                ) : (
                  <>
                    <MediaControlContainer color={color}>
                      <Text variant='heading-emphasis-01' as='h1'>
                        {t('animation-slider.slider')}
                      </Text>
                      <Media
                        currentPosition={currentSlide}
                        total={slides.length}
                        onNavigateNext={navigateToNext}
                        onNavigate={navigateToSlide}
                        isPlaying={isAnimationPlaying}
                        setIsPlaying={setIsAnimationPlaying}
                        color={color}
                      />
                    </MediaControlContainer>
                    {renderTitle()}
                    <Text variant='bordertext-05'>
                      <SafeHtmlContent html={slides[currentSlide].complement} />
                    </Text>
                  </>
                )}
              </Complement>
            </HierarchyCard>
          </CardContainer>
        )}
        {isMobile && (
          <ImageWrapper>
            <img
              src={slides[currentSlide].image.mobile.url}
              alt={slides[currentSlide].image.mobile.alt}
            />
          </ImageWrapper>
        )}
      </Container>
      <SubtitleWrapper>
        {isMobile ? (
          <CollapsibleCard
            leftHeaderElement={
              <Text variant='content-emphasis-04'>
                {t('animation-slider.subtitle')}
              </Text>
            }
            heightClass='small'
          >
            <ImagesContainer>
              {subtitles.map((subtitle, index) => (
                <div key={index}>
                  <img src={subtitle} />
                </div>
              ))}
            </ImagesContainer>
          </CollapsibleCard>
        ) : (
          <HierarchyCard>
            <Subtitle>
              <Text variant='content-emphasis-05'>
                {t('animation-slider.subtitle')}
              </Text>
              <ImagesContainer>
                {subtitles.map((subtitle, index) => (
                  <div key={index}>
                    <img src={subtitle} />
                  </div>
                ))}
              </ImagesContainer>
            </Subtitle>
          </HierarchyCard>
        )}
      </SubtitleWrapper>
    </>
  )
}

export default AnimationSlider
